import {
  ChangeDetectionStrategy,
  Component,
  effect,
  input,
  output,
  signal,
  untracked,
} from '@angular/core';
import { form, FormField as SignalFormField, required } from '@angular/forms/signals';
import { SignalButton, SignalCheckbox, SignalFormDirective, SignalTextField } from '@vallift/ngl';
import type { FormField } from '../../../../models/built-form';

interface CheckboxGroupConfigModel {
  label: string;
  helpText: string;
  options: string[];
  required: boolean;
}

@Component({
  selector: 'app-checkbox-group-config-view',
  standalone: true,
  imports: [SignalFormField, SignalTextField, SignalCheckbox, SignalButton, SignalFormDirective],
  templateUrl: './checkbox-group-config-view.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CheckboxGroupConfigViewComponent {
  readonly field = input.required<FormField>();
  readonly fieldChange = output<FormField>();

  readonly model = signal<CheckboxGroupConfigModel>({
    label: '',
    helpText: '',
    options: [''],
    required: false,
  });

  readonly configForm = form(this.model, (schema) => {
    required(schema.label);
  });

  private lastFieldId: string | null = null;
  private suppressEmit = false;

  constructor() {
    effect(() => {
      const next = this.field();
      if (next.id === this.lastFieldId) {
        return;
      }
      this.lastFieldId = next.id;
      this.suppressEmit = true;
      const validation = next.validation ?? {};
      this.model.set({
        label: next.label,
        helpText: next.helpText ?? '',
        options: next.options?.length ? next.options : [''],
        required: next.required ?? validation.required ?? false,
      });
    });

    effect(() => {
      const draft = this.model();
      if (this.suppressEmit) {
        this.suppressEmit = false;
        return;
      }
      const field = untracked(() => this.field());
      if (!field || field.id !== this.lastFieldId) {
        return;
      }

      this.fieldChange.emit({
        ...field,
        label: draft.label.trim(),
        helpText: draft.helpText.trim() || undefined,
        options: draft.options.map((option) => option.trim()).filter(Boolean),
        validation: {
          required: draft.required,
        },
        required: draft.required,
      });
    });
  }

  addOption(): void {
    this.model.update((current) => ({
      ...current,
      options: [...current.options, ''],
    }));
  }

  removeOption(index: number): void {
    this.model.update((current) => {
      if (current.options.length <= 1) {
        return current;
      }
      return {
        ...current,
        options: current.options.filter((_, i) => i !== index),
      };
    });
  }

}
